import PropTypes from "prop-types";
import * as React from "react";

const SWIPE_THRESHOLD = 50;
const TRANSITION_DURATION = 300;

export default function Carousel({
  children,
  index = 0,
  onChangeIndex,
  autoPlay = false,
  interval = 5000,
  enableMouseEvents = false,
  style,
  className,
}) {
  const items = React.Children.toArray(children);
  if (items.length === 0) return null;
  if (items.length === 1) {
    return (
      <div style={style} className={className}>
        {items[0]}
      </div>
    );
  }
  return (
    <InnerCarousel
      index={index}
      onChangeIndex={onChangeIndex}
      autoPlay={autoPlay}
      interval={interval}
      enableMouseEvents={enableMouseEvents}
      style={style}
      className={className}
      items={items}
    />
  );
}

Carousel.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number,
  onChangeIndex: PropTypes.func,
  autoPlay: PropTypes.bool,
  interval: PropTypes.number,
  enableMouseEvents: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
};

function InnerCarousel({
  items,
  index = 0,
  onChangeIndex,
  autoPlay = false,
  interval = 5000,
  enableMouseEvents = false,
  style,
  className,
}) {
  const containerRef = React.useRef(null);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const dragStartX = React.useRef(0);
  const itemCount = items.length;

  // Internal position includes cloned slides: [lastClone, ...slides, firstClone]
  // So position 0 = lastClone, 1 to itemCount = real slides, itemCount+1 = firstClone
  const [internalPos, setInternalPos] = React.useState(index + 1);
  const [animate, setAnimate] = React.useState(true);

  // Sync internal position with external index
  React.useEffect(() => {
    if (itemCount <= 1) {
      setInternalPos(index + 1);
      return;
    }

    const targetPos = index + 1;
    const currentRealIndex =
      (((internalPos - 1) % itemCount) + itemCount) % itemCount;

    // Determine if we're wrapping
    if (currentRealIndex === itemCount - 1 && index === 0) {
      // Going forward from last to first - animate to the clone at the end
      setAnimate(true);
      setInternalPos(itemCount + 1);
      setIsTransitioning(true);
    } else if (currentRealIndex === 0 && index === itemCount - 1) {
      // Going backward from first to last - animate to the clone at the beginning
      setAnimate(true);
      setInternalPos(0);
      setIsTransitioning(true);
    } else {
      // Normal navigation
      setAnimate(true);
      setInternalPos(targetPos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, itemCount]);

  // After transitioning to a clone, jump to the real slide without animation
  React.useEffect(() => {
    if (!isTransitioning) return;

    const timer = setTimeout(() => {
      setAnimate(false);
      if (internalPos === 0) {
        setInternalPos(itemCount);
      } else if (internalPos === itemCount + 1) {
        setInternalPos(1);
      }
      setIsTransitioning(false);
      // Re-enable animation after the jump
      requestAnimationFrame(() => {
        setAnimate(true);
      });
    }, TRANSITION_DURATION);

    return () => clearTimeout(timer);
  }, [isTransitioning, internalPos, itemCount]);

  // Auto-play
  React.useEffect(() => {
    if (!autoPlay || itemCount <= 1 || isTransitioning) return;

    const timer = setInterval(() => {
      onChangeIndex?.((index + 1) % itemCount);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, interval, index, itemCount, onChangeIndex, isTransitioning]);

  const handleDragStart = React.useCallback((clientX) => {
    setIsDragging(true);
    dragStartX.current = clientX;
  }, []);

  const handleDragMove = React.useCallback(
    (clientX) => {
      if (!isDragging) return;
      const diff = clientX - dragStartX.current;
      setDragOffset(diff);
    },
    [isDragging],
  );

  const handleDragEnd = React.useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    if (Math.abs(dragOffset) > SWIPE_THRESHOLD) {
      if (dragOffset > 0) {
        // Swiped right - go to previous
        onChangeIndex?.((index - 1 + itemCount) % itemCount);
      } else {
        // Swiped left - go to next
        onChangeIndex?.((index + 1) % itemCount);
      }
    }

    setDragOffset(0);
  }, [isDragging, dragOffset, index, itemCount, onChangeIndex]);

  const onTouchStart = React.useCallback(
    (e) => handleDragStart(e.touches[0].clientX),
    [handleDragStart],
  );
  const onTouchMove = React.useCallback(
    (e) => handleDragMove(e.touches[0].clientX),
    [handleDragMove],
  );
  const onTouchEnd = React.useCallback(() => handleDragEnd(), [handleDragEnd]);

  const onMouseDown = React.useCallback(
    (e) => {
      if (!enableMouseEvents) return;
      e.preventDefault();
      handleDragStart(e.clientX);
    },
    [enableMouseEvents, handleDragStart],
  );
  const onMouseMove = React.useCallback(
    (e) => {
      if (!enableMouseEvents) return;
      handleDragMove(e.clientX);
    },
    [enableMouseEvents, handleDragMove],
  );
  const onMouseUp = React.useCallback(() => {
    if (!enableMouseEvents) return;
    handleDragEnd();
  }, [enableMouseEvents, handleDragEnd]);
  const onMouseLeave = React.useCallback(() => {
    if (!enableMouseEvents || !isDragging) return;
    handleDragEnd();
  }, [enableMouseEvents, isDragging, handleDragEnd]);

  // Build slides array with clones for infinite loop
  const slides = React.useMemo(() => {
    return [
      items[itemCount - 1], // Clone of last slide at beginning
      ...items,
      items[0], // Clone of first slide at end
    ];
  }, [items, itemCount]);

  const translateX =
    -internalPos * 100 +
    (dragOffset / (containerRef.current?.offsetWidth || 1)) * 100;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ overflow: "hidden", ...style }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
    >
      <div
        style={{
          display: "flex",
          transition:
            isDragging || !animate
              ? "none"
              : `transform ${TRANSITION_DURATION}ms ease-out`,
          transform: `translateX(${translateX}%)`,
          willChange: "transform",
        }}
      >
        {slides.map((item, i) => (
          <div
            key={i}
            style={{
              flex: "0 0 100%",
              minWidth: 0,
            }}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

InnerCarousel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.node).isRequired,
  index: PropTypes.number,
  onChangeIndex: PropTypes.func,
  autoPlay: PropTypes.bool,
  interval: PropTypes.number,
  enableMouseEvents: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
};
